import React, { useEffect, useRef } from "react";
import { createAuraParticles, renderAuraFrame } from "./particle/auraHelpers";

/**
 * LogoAura — Canvas particle aura that emanates from the logo.
 * - Disabled on touch / coarse-pointer devices
 * - Disabled when prefers-reduced-motion is set
 *
 * Heavy draw logic lives in auraHelpers so this component remains
 * focused on lifecycle (resize observer, cursor tracking, RAF cleanup).
 */
const LogoAura = ({ size = 320 }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    target: { x: 0.5, y: 0.5 },
    cur: { x: 0.5, y: 0.5 },
    particles: [],
    raf: 0,
  });

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const noMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || noMotion) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    stateRef.current.particles = createAuraParticles();

    // Keep the aura tethered close to the logo. We translate the cursor's
    // canvas-relative position into a small offset from center (≤ ±10%) so
    // the glow tracks the mouse subtly without drifting away from the logo.
    const OFFSET_SCALE = 0.18; // how much the aura responds to cursor
    const MAX_OFFSET = 0.1; // hard cap (10% of canvas) from center
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width - 0.5;
      const ny = (e.clientY - rect.top) / rect.height - 0.5;
      const offX = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, nx * OFFSET_SCALE));
      const offY = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, ny * OFFSET_SCALE));
      stateRef.current.target.x = 0.5 + offX;
      stateRef.current.target.y = 0.5 + offY;
    };
    window.addEventListener("mousemove", onMove);

    // Only animate while the canvas is actually on screen. Without this
    // the aura keeps burning a full rAF loop while the visitor scrolls
    // through the rest of the page.
    let visible = false;
    const draw = () => {
      if (!visible) return;
      renderAuraFrame(ctx, canvas, stateRef.current, dpr);
      stateRef.current.raf = requestAnimationFrame(draw);
    };
    const io = new IntersectionObserver(([entry]) => {
      const nowVisible = entry.isIntersecting;
      if (nowVisible && !visible) {
        visible = true;
        stateRef.current.raf = requestAnimationFrame(draw);
      } else if (!nowVisible) {
        visible = false;
        cancelAnimationFrame(stateRef.current.raf);
      }
    });
    io.observe(canvas);

    // Capture the ref's current object so the cleanup function can cancel
    // the RAF safely even if `stateRef.current` is reassigned later.
    const stateAtMount = stateRef.current;
    return () => {
      cancelAnimationFrame(stateAtMount.raf);
      window.removeEventListener("mousemove", onMove);
      ro.disconnect();
      io.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="logo-aura-canvas"
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: `-${Math.round(size * 0.55)}px`,
        width: `${size + size * 1.1}px`,
        height: `${size + size * 1.1}px`,
        pointerEvents: "none",
        zIndex: 0,
        mixBlendMode: "screen",
      }}
    />
  );
};

export default LogoAura;
